import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CRED_LABELS, formatDate } from "@/lib/constants";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { useDropzone } from "react-dropzone";
import { CloudArrowUp, FileText, CheckCircle, XCircle, Hourglass } from "@phosphor-icons/react";

export default function MyFile() {
  const [me, setMe] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [recentUploads, setRecentUploads] = useState([]);

  const load = async () => {
    try {
      const r = await api.get("/my/profile");
      setMe(r.data);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error("No practitioner file is linked to your account yet. Contact your credentialing coordinator.");
      } else {
        toast.error(err?.response?.data?.detail || "Could not load your profile");
      }
    }
  };

  useEffect(() => { load(); }, []);

  const onDrop = useCallback(async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const successes = [];
    for (const f of files) {
      const form = new FormData();
      form.append("file", f);
      try {
        const r = await api.post("/my/documents/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        successes.push(r.data);
      } catch (err) {
        toast.error(`${f.name}: ${err?.response?.data?.detail || "Upload failed"}`);
      }
    }
    if (successes.length) {
      setRecentUploads((prev) => [...successes, ...prev].slice(0, 10));
      toast.success(`${successes.length} document${successes.length === 1 ? "" : "s"} uploaded for coordinator review`);
      load();
    }
    setUploading(false);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [], "application/pdf": [".pdf"] },
    disabled: uploading,
  });

  if (!me) return <div className="text-gray-500" data-testid="my-file-loading">Loading your file…</div>;

  return (
    <div className="space-y-6" data-testid="my-file-page">
      <header className="border-b border-gray-200 pb-6">
        <div className="overline text-gray-500">Practitioner self-service portal</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 mt-1">
          Welcome, {me.first_name} {me.last_name}
        </h1>
        <div className="text-sm text-gray-600 mt-2">{me.title}{me.specialty ? ` · ${me.specialty}` : ""}</div>
        <div className="mt-4"><StatusBadge status={me.status_summary?.worst_status || "NO_DATE"} /></div>
      </header>

      <section className="bg-white border border-gray-200 p-6" data-testid="my-upload-section">
        <div className="overline text-gray-500">Upload renewed credentials</div>
        <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5 mb-4">Submit a new scan</h2>
        <p className="text-sm text-gray-600 mb-4 max-w-2xl">
          Drop a renewed license, BLS/ACLS card, immunization record, or any credential document.
          The system will read the dates with OCR. Your credentialing coordinator will review and attach
          it to your file.
        </p>
        <div
          {...getRootProps()}
          className={`border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${
            isDragActive ? "border-slate-900 bg-slate-50" : "border-gray-300 hover:border-slate-700 hover:bg-gray-50"
          } ${uploading ? "opacity-60 cursor-wait" : ""}`}
          data-testid="my-dropzone"
        >
          <input {...getInputProps()} data-testid="my-file-input" />
          <CloudArrowUp size={48} weight="duotone" className="mx-auto text-slate-400 mb-3" />
          <div className="text-sm font-semibold text-slate-900">
            {uploading ? "Uploading…" : isDragActive ? "Drop files to upload" : "Drag & drop, or click to browse"}
          </div>
          <div className="text-xs text-gray-500 mt-1">PDF, JPG, PNG · max ~20MB each</div>
        </div>
        {recentUploads.length > 0 && (
          <div className="mt-5 border border-gray-200 bg-gray-50" data-testid="my-recent-uploads">
            <div className="px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-gray-600 border-b border-gray-200">
              Just submitted (awaiting coordinator review)
            </div>
            <ul className="divide-y divide-gray-200">
              {recentUploads.map((d) => (
                <li key={d.id} className="px-4 py-3 text-sm flex items-center gap-3">
                  <FileText size={16} className="text-slate-500" />
                  <span className="font-medium text-slate-900 flex-1 truncate">{d.original_filename}</span>
                  <span className="text-xs text-gray-500 font-mono">
                    {d.ocr_data?.document_type || "OCR queued"}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-900 text-[11px] font-mono">
                    <Hourglass size={12} weight="bold" /> PENDING REVIEW
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className="bg-white border border-gray-200" data-testid="my-credentials-section">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="overline text-gray-500">Your credential ledger</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5">All tracked items</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="my-credentials-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-gray-500 border-b border-gray-200">
                <th className="px-6 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold">Issuer</th>
                <th className="px-4 py-3 font-semibold">Issued</th>
                <th className="px-4 py-3 font-semibold">Expires</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Verification</th>
              </tr>
            </thead>
            <tbody>
              {(me.credentials || []).length === 0 && (
                <tr><td colSpan="6" className="p-10 text-center text-gray-500 text-sm">No credentials yet.</td></tr>
              )}
              {(me.credentials || []).map((c) => (
                <tr key={c.id} className="data-row border-b border-gray-100">
                  <td className="px-6 py-3 font-medium text-slate-900">{CRED_LABELS[c.credential_type] || c.credential_type}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{c.issuer || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{formatDate(c.issue_date)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{formatDate(c.expiration_date)}</td>
                  <td className="px-4 py-3"><StatusBadge status={credStatus(c.expiration_date)} /></td>
                  <td className="px-4 py-3 text-xs font-mono">
                    <span className={`inline-block px-2 py-0.5 ${c.psv_status === "VERIFIED" ? "bg-emerald-100 text-emerald-800" : c.psv_status === "PENDING" ? "bg-sky-100 text-sky-800" : "bg-gray-100 text-gray-700"}`}>
                      {c.psv_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-white border border-gray-200 p-6" data-testid="my-documents-section">
        <div className="overline text-gray-500">Documents on file</div>
        <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5 mb-4">Submitted scans</h2>
        {(me.documents || []).length === 0 ? (
          <div className="text-sm text-gray-500">No documents yet. Upload your first scan above.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {(me.documents || []).map((d) => (
              <li key={d.id} className="py-2 text-sm flex items-center gap-3">
                <FileText size={16} className="text-slate-500" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">{d.original_filename}</div>
                  <div className="text-xs text-gray-500 font-mono">{d.content_type} · {(d.size/1024).toFixed(1)} KB · {formatDate(d.created_at)}</div>
                </div>
                {d.submitted_by_practitioner && (
                  <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 bg-blue-100 text-blue-900">Self-submitted</span>
                )}
                {d.review_status === "APPROVED" ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><CheckCircle size={14} weight="bold" /> Approved</span>
                ) : d.review_status === "REJECTED" ? (
                  <span className="inline-flex items-center gap-1 text-red-700 text-xs"><XCircle size={14} weight="bold" /> Needs revision</span>
                ) : d.review_status === "PENDING" ? (
                  <span className="inline-flex items-center gap-1 text-amber-700 text-xs"><Hourglass size={14} weight="bold" /> Pending review</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function credStatus(exp) {
  if (!exp) return "NO_DATE";
  const d = Math.round((new Date(exp) - new Date()) / (1000 * 60 * 60 * 24));
  if (d < 0) return "EXPIRED";
  if (d <= 7) return "EXPIRING_7";
  if (d <= 30) return "EXPIRING_30";
  if (d <= 60) return "EXPIRING_60";
  if (d <= 90) return "EXPIRING_90";
  return "CURRENT";
}
